import React, { useState, useCallback } from "react";
import { t, Trans } from "@lingui/macro";
import cx from "classnames";
import { INCREASE, DECREASE, USD_DECIMALS, getOrderError, getPositionForOrder, importImage } from "lib/legacy";
import Checkbox from "components/Checkbox/Checkbox";
import { handleCancelOrder } from "domain/legacy";
import Tooltip from "components/Tooltip/Tooltip";
import OrderEditor from "components/Exchange/OrderEditor";

import StatsTooltipRow from "components/StatsTooltip/StatsTooltipRow";
import { TRIGGER_PREFIX_ABOVE, TRIGGER_PREFIX_BELOW } from "config/ui";
import { getTokenInfo, getUsd } from "domain/tokens/utils";
import { formatAmount } from "lib/numbers";
import ExternalLink from "components/ExternalLink/ExternalLink";
import connectWalletImg from "img/ic_wallet_24.svg";

function getOrderTitle(order) {
  const sizeDeltaText = formatAmount(order.sizeDelta, USD_DECIMALS, 2, true);
  return `$${sizeDeltaText}`;
}

export default function OrdersListPortfolio(props) {
  const {
    account,
    library,
    setPendingTxns,
    pendingTxns,
    infoTokens,
    positionsMap,
    totalTokenWeights,
    usdgSupply,
    orders,
    hideActions,
    chainId,
    savedShouldDisableValidationForTesting,
    cancelOrderIdList,
    setCancelOrderIdList,
    active,
  } = props;

  const [editingOrder, setEditingOrder] = useState(null);

  const onCancelClick = useCallback(
    (order) => {
      handleCancelOrder(chainId, library, order, { pendingTxns, setPendingTxns });
    },
    [library, pendingTxns, setPendingTxns, chainId]
  );

  const onEditClick = useCallback(
    (order) => {
      setEditingOrder(order);
    },
    [setEditingOrder]
  );

  const noPositionIcon = importImage("No-position-order.png");

  const renderHead = useCallback(() => {
    return (
      <tr className="Exchange-list-header">
        <th className={!hideActions && orders.length > 0 ? "Start" : "Start"}>
          <div>
            <Trans>Position</Trans>
          </div>
        </th>

        <th>
          <div>
            <Trans>Side</Trans>
          </div>
        </th>

        <th>
          <div>
            <Trans>Order Type</Trans>
          </div>
        </th>
        <th>
          <div>
            <Trans>Size</Trans>
          </div>
        </th>
        <th>
          <div>
            <Trans>Price</Trans>
          </div>
        </th>
        <th>
          <div>
            <Trans>Mark Price</Trans>
          </div>
        </th>

        <th className="End"></th>
      </tr>
    );
  }, [orders, hideActions]);

  const renderEmptyRow = useCallback(() => {
    const connectWallet = () => {
      if (!active) {
        props.connectWallet();
        return;
      }
    };
    if (orders && orders.length) {
      return null;
    }

    if (active) {
      return (
        <tr>
          <td colSpan="16">
            <div className="Exchange-empty-positions-list-note Exchange-list-no-position">
              <div className="Exchange-empty-positions">
                <img src={noPositionIcon} alt="No position" className="no-position-icon" />
                <p>You don't have any orders.</p>
              </div>
            </div>
          </td>
        </tr>
      );
    } else {
      return (
        <tr>
          <td colSpan="15">
            <span className="Exchange-empty-positions-list-note Exchange-list-no-position">
              <div className="Exchange-inactive-wrapper">
                <p>Please connect your wallet.</p>
                <button className="Exchange-inactive" onClick={() => connectWallet()}>
                  <img src={connectWalletImg} alt="No position" />
                  <p className="Exchange-connect-wallet">Connect Wallet</p>
                </button>
              </div>
            </span>
          </td>
        </tr>
      );
    }
  }, [orders, noPositionIcon, active, props]);

  const renderActions = useCallback(
    (order) => {
      return (
        <>
          <td className="">
            <div className="Exchange-list-buttons">
              <button className="Exchange-list-action" onClick={() => onEditClick(order)}>
                <Trans>Edit</Trans>
              </button>

              <button className="Exchange-list-action" onClick={() => onCancelClick(order)}>
                <Trans>Cancel</Trans>
              </button>
            </div>
          </td>
        </>
      );
    },
    [onEditClick, onCancelClick]
  );

  const renderLargeList = useCallback(() => {
    if (!orders || !orders.length) {
      return null;
    }

    return orders.map((order) => {
      const indexToken = getTokenInfo(infoTokens, order.indexToken);

      // Longs Increase: max price
      // Longs Decrease: min price
      // Short Increase: min price
      // Short Decrease: max price
      const maximisePrice = (order.type === INCREASE && order.isLong) || (order.type === DECREASE && !order.isLong);

      const markPrice = maximisePrice ? indexToken.contractMaxPrice : indexToken.contractMinPrice;
      const triggerPricePrefix = order.triggerAboveThreshold ? TRIGGER_PREFIX_ABOVE : TRIGGER_PREFIX_BELOW;
      const indexTokenSymbol = indexToken.isWrapped ? indexToken.baseSymbol : indexToken.symbol;
      const orderId = `${order.type}-${order.index}`;

      const error = getOrderError(account, order, positionsMap);
      const orderTitle = getOrderTitle(order, indexTokenSymbol);

      const icon = importImage("ic_" + indexTokenSymbol.toLowerCase() + "_40.svg");

      const orderText = (
        <>
          {error ? (
            <Tooltip
              className="order-error"
              handle={orderTitle}
              position="right-bottom"
              handleClassName="plain"
              renderContent={() => <span className="negative">{error}</span>}
            />
          ) : (
            orderTitle
          )}
        </>
      );

      return (
        <tr className="Exchange-list-body" key={`${order.isLong}-${order.type}-${order.index}`}>
          <td className="clickable Exchange-list-item-type" key={`${order.type}-${order.index}`}>
            <div className="Exchange-list-image-and-title">
              {!hideActions && (
                <div>
                  <Checkbox
                    isChecked={cancelOrderIdList?.includes(orderId)}
                    setIsChecked={() => {
                      setCancelOrderIdList((prevState) => {
                        if (prevState.includes(orderId)) {
                          return prevState.filter((i) => i !== orderId);
                        } else {
                          return prevState.concat(orderId);
                        }
                      });
                    }}
                  />
                </div>
              )}
              <img className="Exchange-list-token" src={icon} alt="Token icon" />
              <div className="Exchange-list-leverage">
                <div className="Exchange-list-title">
                  <p>{indexTokenSymbol}</p>
                </div>

                <div className="Exchange-list-info-label">
                  {order.leverage && (
                    <span className="muted">{"Leverage " + formatAmount(order.leverage, 4, 2, true)}x&nbsp;</span>
                  )}
                </div>
              </div>
            </div>
          </td>
          <td>
            <div>
              <span className={cx({ long: order.isLong, short: !order.isLong })}>
                {order.isLong ? t`Long` : t`Short`}
              </span>
            </div>
          </td>
          <td className="Exchange-list-item-type">{order.type === INCREASE ? t`Limit` : t`Trigger`}</td>
          <td>
            {order.type === DECREASE ? (
              orderText
            ) : (
              <Tooltip
                handle={orderText}
                position="right-bottom"
                renderContent={() => {
                  const collateralTokenInfo = getTokenInfo(infoTokens, order.collateralToken);
                  const collateralUSD = getUsd(order.tokenAmount, order.collateralToken, false, infoTokens);
                  return (
                    <StatsTooltipRow
                      label={t`Collateral`}
                      value={`${formatAmount(collateralUSD, USD_DECIMALS, 2, true)} (${formatAmount(
                        order.tokenAmount,
                        collateralTokenInfo.decimals,
                        4,
                        true
                      )}
                      ${collateralTokenInfo.baseSymbol || collateralTokenInfo.symbol})`}
                    />
                  );
                }}
              />
            )}
          </td>
          <td>
            {triggerPricePrefix} {formatAmount(order.triggerPrice, USD_DECIMALS, 2, true)}
          </td>
          <td>
            <Tooltip
              handle={formatAmount(markPrice, USD_DECIMALS, 2, true)}
              position="right-bottom"
              renderContent={() => {
                return (
                  <Trans>
                    <p>
                      The price that orders can be executed at may differ slightly from the chart price, as market
                      orders update oracle prices, while limit/trigger orders do not.
                    </p>
                    <p>
                      This can also cause limit/triggers to not be executed if the price is not reached for long enough.{" "}
                      <ExternalLink href="https://gmxio.gitbook.io/gmx/trading#stop-loss-take-profit-orders">
                        Read more
                      </ExternalLink>
                      .
                    </p>
                  </Trans>
                );
              }}
            />
          </td>
          {!hideActions && renderActions(order)}
        </tr>
      );
    });
  }, [orders, renderActions, infoTokens, positionsMap, cancelOrderIdList, setCancelOrderIdList, hideActions, account]);

  const renderSmallList = useCallback(() => {
    if (!orders || !orders.length) {
      return null;
    }

    return orders.map((order) => {
      const indexToken = getTokenInfo(infoTokens, order.indexToken);
      const maximisePrice = (order.type === INCREASE && order.isLong) || (order.type === DECREASE && !order.isLong);
      const markPrice = maximisePrice ? indexToken.contractMaxPrice : indexToken.contractMinPrice;
      const triggerPricePrefix = order.triggerAboveThreshold ? TRIGGER_PREFIX_ABOVE : TRIGGER_PREFIX_BELOW;
      const indexTokenSymbol = indexToken.isWrapped ? indexToken.baseSymbol : indexToken.symbol;

      const collateralTokenInfo = getTokenInfo(infoTokens, order.collateralToken);
      const collateralUSD = getUsd(order.tokenAmount, order.collateralToken, true, infoTokens);

      const error = getOrderError(account, order, positionsMap);
      const orderTitle = getOrderTitle(order, indexTokenSymbol);

      return (
        <div key={`${order.isLong}-${order.type}-${order.index}`} className="App-card">
          <div className="App-card-title-small">
            {error ? (
              <Tooltip
                className="order-error"
                handle={orderTitle}
                position="left-bottom"
                handleClassName="plain"
                renderContent={() => <span className="negative">{error}</span>}
              />
            ) : (
              orderTitle
            )}
          </div>
          <div className="App-card-divider"></div>
          <div className="App-card-content">
            <div className="App-card-row">
              <div className="label">
                <Trans>Price</Trans>
              </div>
              <div>
                {triggerPricePrefix} {formatAmount(order.triggerPrice, USD_DECIMALS, 2, true)}
              </div>
            </div>
            <div className="App-card-row">
              <div className="label">
                <Trans>Mark Price</Trans>
              </div>
              <div>
                <Tooltip
                  handle={formatAmount(markPrice, USD_DECIMALS, 2, true)}
                  position="right-bottom"
                  renderContent={() => {
                    return (
                      <Trans>
                        The price that the order can be executed at may differ slightly from the chart price as market
                        orders can change the price while limit / trigger orders cannot.
                      </Trans>
                    );
                  }}
                />
              </div>
            </div>
            {order.type === INCREASE && (
              <div className="App-card-row">
                <div className="label">
                  <Trans>Collateral</Trans>
                </div>
                <div>
                  ${formatAmount(collateralUSD, USD_DECIMALS, 2, true)} (
                  {formatAmount(order.tokenAmount, collateralTokenInfo.decimals, 4, true)}{" "}
                  {collateralTokenInfo.baseSymbol || collateralTokenInfo.symbol})
                </div>
              </div>
            )}
            {!hideActions && (
              <>
                <div className="App-card-divider"></div>
                <div className="App-card-options">
                  <button className="App-button-option App-card-option" onClick={() => onEditClick(order)}>
                    <Trans>Edit</Trans>
                  </button>
                  <button className="App-button-option App-card-option" onClick={() => onCancelClick(order)}>
                    <Trans>Cancel</Trans>
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      );
    });
  }, [orders, onEditClick, onCancelClick, infoTokens, positionsMap, hideActions, account]);

  return (
    <React.Fragment>
      <div>
        <table className="Exchange-list Orders App-box large">
          <tbody>
            {renderHead()}
            {renderEmptyRow()}
            {renderLargeList()}
          </tbody>
        </table>
        <div className="Exchange-list Orders small">
          {(!orders || orders.length === 0) && (
            <div className="Exchange-empty-positions-list-note App-card">
              <div className="Exchange-empty-positions">
                <p>You don't have any positions.</p>
              </div>
            </div>
          )}
          {renderSmallList()}
        </div>
        {editingOrder && (
          <OrderEditor
            account={account}
            order={editingOrder}
            setEditingOrder={setEditingOrder}
            infoTokens={infoTokens}
            pendingTxns={pendingTxns}
            setPendingTxns={setPendingTxns}
            getPositionForOrder={getPositionForOrder}
            positionsMap={positionsMap}
            library={library}
            totalTokenWeights={totalTokenWeights}
            usdgSupply={usdgSupply}
            savedShouldDisableValidationForTesting={savedShouldDisableValidationForTesting}
          />
        )}
      </div>
    </React.Fragment>
  );
}
